/*
 * CAPTURE-OWNED MODULE
 * AnchorMatchSettings — inline settings for anchor zone matching
 * Rendered in the Properties panel when an anchor zone is selected.
 * Allows editing anchor_text, anchor_match_mode, anchor_regex,
 * anchor_fuzzy_threshold, and anchor_alternatives.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  suggestAnchorPattern,
  validateRegexPattern,
  testPatternAgainstText,
  generateAlternatives,
} from '../../utils/anchorPatternUtils';
import './AnchorMatchSettings.css';

const MATCH_MODES = [
  { value: 'contains', label: 'Contains', description: 'Anchor text found anywhere in OCR text (default)' },
  { value: 'exact', label: 'Exact', description: 'OCR text must match anchor exactly (case-insensitive)' },
  { value: 'fuzzy', label: 'Fuzzy', description: 'Matches similar text within a similarity threshold' },
  { value: 'regex', label: 'Regex', description: 'Match using a regular expression pattern' },
];

const AnchorMatchSettings = ({
  zone,                // The selected anchor zone object
  ocrTextPreview,      // Text currently in the zone (from OCR)
  onUpdate,            // (zoneId, updates) => true | { ok, zone?, error? } — persists changes
}) => {
  // Local state for editing (not persisted until Save)
  const [anchorText, setAnchorText] = useState(zone?.anchor_text || '');
  const [matchMode, setMatchMode] = useState(zone?.anchor_match_mode || 'contains');
  const [regexPattern, setRegexPattern] = useState(zone?.anchor_regex || '');
  const [fuzzyThreshold, setFuzzyThreshold] = useState(zone?.anchor_fuzzy_threshold ?? 0.80);
  const [alternatives, setAlternatives] = useState(zone?.anchor_alternatives || []);
  const [newAltText, setNewAltText] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Reset local state when zone changes
  useEffect(() => {
    if (!zone) return;
    setAnchorText(zone.anchor_text || '');
    setMatchMode(zone.anchor_match_mode || 'contains');
    setRegexPattern(zone.anchor_regex || '');
    setFuzzyThreshold(zone.anchor_fuzzy_threshold ?? 0.80);
    setAlternatives(zone.anchor_alternatives || []);
    setDirty(false);
    setSaveError(null);
  }, [zone?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mark dirty on any local change
  const markDirty = useCallback(() => { setDirty(true); setSaveError(null); }, []);

  // Regex validation
  const regexValidation = useMemo(() => {
    if (matchMode !== 'regex' || !regexPattern) return null;
    return validateRegexPattern(regexPattern);
  }, [matchMode, regexPattern]);

  // Regex test against OCR preview text
  const regexTestResult = useMemo(() => {
    if (matchMode !== 'regex' || !regexPattern || !ocrTextPreview) return null;
    return testPatternAgainstText(regexPattern, ocrTextPreview);
  }, [matchMode, regexPattern, ocrTextPreview]);

  // Save handler
  const handleSave = useCallback(async () => {
    if (!zone || !onUpdate) return;
    setSaving(true);
    setSaveError(null);
    try {
      const result = await onUpdate(zone.id, {
        anchor_text: anchorText || null,
        anchor_match_mode: matchMode,
        anchor_regex: matchMode === 'regex' ? (regexPattern || null) : zone.anchor_regex,
        anchor_fuzzy_threshold: matchMode === 'fuzzy' ? fuzzyThreshold : zone.anchor_fuzzy_threshold,
        anchor_alternatives: alternatives,
      });
      // Only clear dirty when the save actually persisted. On failure keep the
      // form dirty (no false success) and show the server's reason here.
      const ok = result === true || result?.ok === true;
      if (ok) {
        setDirty(false);
        // A blank anchor text is filled by the server from the words under
        // the box; show what it stored.
        if (result?.zone && typeof result.zone.anchor_text === 'string') setAnchorText(result.zone.anchor_text);
      } else if (result?.error) {
        setSaveError(result.error);
      }
    } catch (err) {
      console.error('[AnchorMatchSettings] save failed:', err);
    } finally {
      setSaving(false);
    }
  }, [zone, onUpdate, anchorText, matchMode, regexPattern, fuzzyThreshold, alternatives]);

  // AI Suggest regex pattern
  const handleSuggestPattern = useCallback(() => {
    if (!anchorText) return;
    const suggested = suggestAnchorPattern(anchorText);
    setRegexPattern(suggested);
    setMatchMode('regex');
    markDirty();
  }, [anchorText, markDirty]);

  // AI Suggest alternatives
  const handleSuggestAlternatives = useCallback(() => {
    if (!anchorText) return;
    const suggested = generateAlternatives(anchorText);
    // Merge with existing, deduplicate
    const merged = [...new Set([...alternatives, ...suggested])];
    setAlternatives(merged);
    markDirty();
  }, [anchorText, alternatives, markDirty]);

  // Add alternative manually
  const handleAddAlternative = useCallback(() => {
    const trimmed = newAltText.trim();
    if (!trimmed || alternatives.includes(trimmed)) return;
    setAlternatives(prev => [...prev, trimmed]);
    setNewAltText('');
    markDirty();
  }, [newAltText, alternatives, markDirty]);

  // Remove alternative
  const handleRemoveAlternative = useCallback((index) => {
    setAlternatives(prev => prev.filter((_, i) => i !== index));
    markDirty();
  }, [markDirty]);

  if (!zone) return null;

  // Only show for anchor zones (or show a subset for field zones)
  const isAnchorZone = zone.zone_type === 'anchor';

  return (
    <div className="ams-container">
      <div className="ams-header">
        <h4 className="ams-title">Match Settings</h4>
        {dirty && (
          <button
            className="ams-save-btn"
            onClick={handleSave}
            disabled={saving || (matchMode === 'regex' && regexValidation && !regexValidation.valid)}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}
      </div>
      {saveError && (
        <div className="ams-error" role="alert">{saveError}</div>
      )}

      {/* Anchor Text */}
      <div className="ams-field">
        <label className="ams-label">Anchor Text</label>
        <input
          type="text"
          className="ams-input"
          value={anchorText}
          onChange={(e) => { setAnchorText(e.target.value); markDirty(); }}
          placeholder="e.g., Invoice Number:"
        />
        <p className="ams-hint">
          The text the system looks for to locate this field on the document.
        </p>
      </div>

      {/* Match Mode */}
      <div className="ams-field">
        <label className="ams-label">Match Mode</label>
        <select
          className="ams-select"
          value={matchMode}
          onChange={(e) => { setMatchMode(e.target.value); markDirty(); }}
        >
          {MATCH_MODES.map(m => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
        <p className="ams-hint">
          {MATCH_MODES.find(m => m.value === matchMode)?.description}
        </p>
      </div>

      {/* Regex Pattern (visible only in regex mode) */}
      {matchMode === 'regex' && (
        <div className="ams-field">
          <div className="ams-field-header">
            <label className="ams-label">Regex Pattern</label>
            <button
              className="ams-action-btn"
              onClick={handleSuggestPattern}
              title="Generate an OCR-tolerant regex from the anchor text"
            >
              ✨ Suggest
            </button>
          </div>
          <input
            type="text"
            className={`ams-input ams-input-mono ${regexValidation && !regexValidation.valid ? 'ams-input-error' : ''}`}
            value={regexPattern}
            onChange={(e) => { setRegexPattern(e.target.value); markDirty(); }}
            placeholder="e.g., [Il1]nvoice\\s+Number[:;]?"
          />
          {regexValidation && !regexValidation.valid && (
            <p className="ams-error">{regexValidation.error}</p>
          )}
          {regexTestResult && (
            <div className={`ams-test-result ${regexTestResult.matched ? 'ams-test-pass' : 'ams-test-fail'}`}>
              {regexTestResult.matched
                ? <>✓ Matches: "<strong>{regexTestResult.matchText}</strong>"</>
                : '✗ No match against current zone text'
              }
            </div>
          )}
        </div>
      )}

      {/* Fuzzy Threshold (visible only in fuzzy mode) */}
      {matchMode === 'fuzzy' && (
        <div className="ams-field">
          <label className="ams-label">
            Similarity Threshold: {Math.round(fuzzyThreshold * 100)}%
          </label>
          <input
            type="range"
            className="ams-slider"
            min="0.50"
            max="1.00"
            step="0.05"
            value={fuzzyThreshold}
            onChange={(e) => { setFuzzyThreshold(parseFloat(e.target.value)); markDirty(); }}
          />
          <div className="ams-slider-labels">
            <span>50% (loose)</span>
            <span>100% (exact)</span>
          </div>
          <p className="ams-hint">
            Lower values tolerate more OCR misreads. 80% is recommended for most documents.
          </p>
        </div>
      )}

      {/* Alternative Anchor Texts */}
      {isAnchorZone && (
        <div className="ams-field">
          <div className="ams-field-header">
            <label className="ams-label">Alternative Anchors</label>
            <button
              className="ams-action-btn"
              onClick={handleSuggestAlternatives}
              title="Generate common OCR misread alternatives"
            >
              ✨ Suggest
            </button>
          </div>
          <p className="ams-hint">
            If the primary anchor text fails, these alternatives are tried in order.
          </p>
          {alternatives.length > 0 && (
            <div className="ams-alt-list">
              {alternatives.map((alt, idx) => (
                <div key={idx} className="ams-alt-item">
                  <span className="ams-alt-text">"{alt}"</span>
                  <button
                    className="ams-alt-remove"
                    onClick={() => handleRemoveAlternative(idx)}
                    title="Remove alternative"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="ams-alt-add">
            <input
              type="text"
              className="ams-input ams-input-sm"
              value={newAltText}
              onChange={(e) => setNewAltText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddAlternative(); } }}
              placeholder="Add custom alternative…"
            />
            <button
              className="ams-add-btn"
              onClick={handleAddAlternative}
              disabled={!newAltText.trim()}
            >
              Add
            </button>
          </div>
        </div>
      )}

      {/* OCR Preview comparison */}
      {ocrTextPreview && (
        <div className="ams-field">
          <label className="ams-label">Current OCR Text in Zone</label>
          <div className="ams-ocr-preview">{ocrTextPreview}</div>
        </div>
      )}
    </div>
  );
};

export default AnchorMatchSettings;
